import React, { useMemo, useState } from "react";
import { Container, LoginButton } from "../../styles/Styles";
import AdminNav from "./AdminNav";
import { useMutation, useQuery } from "@apollo/client";
import {
    DELETE_GUIDE_INVOICE,
    GET_GUIDE_INVOICES,
} from "../gql/guide-invoices-gql";
import { GET_GUIDES_LIST } from "../gql/guides-gql";
import ReviewMonthCalendar from "../common/ReviewMonthCalendar";
import GuideInvoiceRow from "../common/GuideInvoiceRow";
import { uploadGuideInvoiceAdmin } from "../common/uploadGuideInvoice";
import { GuideInvoice } from "../common/interfaces";
import Loader from "../common/Loader";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../common/roles";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment/moment";

const Section = styled.div`
    background: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Select = styled.select`
    width: 100%;
    max-width: 400px;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
`;

const Input = styled.input`
    width: 100%;
    max-width: 400px;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
`;

const GuideSection = styled.div`
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }
`;

const GuideHeading = styled.h3`
    margin: 0 0 1rem 0;
    color: #333;
`;

const InvoiceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const groupInvoicesByGuide = (invoices: GuideInvoice[]) => {
    const groups = new Map<string, { guideName: string; invoices: GuideInvoice[] }>();

    invoices.forEach((invoice) => {
        const guideId = invoice.guide._id;
        const guideName = `${invoice.guide.profile.firstName} ${invoice.guide.profile.lastName}`;
        const existing = groups.get(guideId);

        if (existing) {
            existing.invoices.push(invoice);
        } else {
            groups.set(guideId, { guideName, invoices: [invoice] });
        }
    });

    return Array.from(groups.values()).sort((a, b) =>
        a.guideName.localeCompare(b.guideName)
    );
};

const AdminDashboardInvoices = () => {
    const { user, isAuthenticated } = useAuth();
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectedGuideId, setSelectedGuideId] = useState("");
    const [formMonth, setFormMonth] = useState(currentMonth.getMonth() + 1);
    const [formYear, setFormYear] = useState(currentMonth.getFullYear());
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [uploading, setUploading] = useState(false);

    const queryVars = {
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear(),
    };

    const { data, loading, refetch } = useQuery(GET_GUIDE_INVOICES, {
        variables: queryVars,
        skip: !isAuthenticated,
    });

    const { data: guidesList, loading: guidesLoading } = useQuery(GET_GUIDES_LIST);

    const [deleteGuideInvoice] = useMutation(DELETE_GUIDE_INVOICE, {
        onCompleted: () => {
            toast.success("Invoice deleted successfully!");
            refetch();
        },
        onError: (error) => {
            toast.error("Failed to delete invoice: " + error.message);
        },
    });

    const groupedInvoices = useMemo(() => {
        const invoices: GuideInvoice[] = data?.getGuideInvoices || [];
        return groupInvoicesByGuide(invoices);
    }, [data?.getGuideInvoices]);

    const guides = guidesList?.getGuidesList || [];

    const resetInvoiceForm = (month: number, year: number) => {
        setSelectedGuideId("");
        setSelectedFile(null);
        setFileInputKey((prev) => prev + 1);
        setFormMonth(month);
        setFormYear(year);
    };

    const handleMonthChange = (date: Date) => {
        setCurrentMonth(date);
        setFormMonth(date.getMonth() + 1);
        setFormYear(date.getFullYear());
    };

    const handleUpload = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!selectedGuideId) {
            toast.warning("Please select a guide");
            return;
        }

        if (!selectedFile) {
            toast.warning("Please select a PDF file to upload");
            return;
        }

        if (selectedFile.type !== "application/pdf") {
            toast.warning("Only PDF files are allowed");
            return;
        }

        setUploading(true);

        const uploadedMonth = formMonth;
        const uploadedYear = formYear;

        try {
            await uploadGuideInvoiceAdmin({
                file: selectedFile,
                guideId: selectedGuideId,
                month: uploadedMonth,
                year: uploadedYear,
            });
            toast.success("Invoice uploaded successfully!");

            const uploadedDate = new Date(uploadedYear, uploadedMonth - 1, 1);
            setCurrentMonth(uploadedDate);
            resetInvoiceForm(uploadedMonth, uploadedYear);

            await refetch({
                month: uploadedMonth,
                year: uploadedYear,
            });
        } catch (error: any) {
            const status = error?.response?.status;
            const message = error?.response?.data?.message;

            if (status === 409) {
                toast.error("Invoice already exists for that month");
            } else {
                toast.error(message || error?.message || "Failed to upload invoice");
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (invoiceId: string) => {
        await deleteGuideInvoice({
            variables: { id: invoiceId },
        });
    };

    if (!isAuthenticated || user?.role[0] !== Roles.ADMIN) {
        return <Navigate to="/" />;
    }

    if (loading || guidesLoading) {
        return <Loader />;
    }

    return (
        <Container>
            <AdminNav />
            <h2>Invoices</h2>

            <ReviewMonthCalendar
                currentDate={currentMonth}
                onMonthChange={handleMonthChange}
            />

            <Section>
                <h3>Add Invoice</h3>
                <form onSubmit={handleUpload}>
                    <FormGroup>
                        <Label htmlFor="guideId">Guide</Label>
                        <Select
                            id="guideId"
                            value={selectedGuideId}
                            onChange={(e) => setSelectedGuideId(e.target.value)}
                        >
                            <option value="">Select a guide</option>
                            {guides.map((guide: any) => (
                                <option key={guide._id} value={guide._id}>
                                    {guide.profile.firstName} {guide.profile.lastName}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="month">Month</Label>
                        <Select
                            id="month"
                            value={formMonth}
                            onChange={(e) => setFormMonth(Number(e.target.value))}
                        >
                            {Array.from({ length: 12 }, (_, index) => (
                                <option key={index + 1} value={index + 1}>
                                    {moment().month(index).format("MMMM")}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="year">Year</Label>
                        <Input
                            id="year"
                            type="number"
                            min={2000}
                            max={2100}
                            value={formYear}
                            onChange={(e) => setFormYear(Number(e.target.value))}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="file">PDF Invoice</Label>
                        <Input
                            key={fileInputKey}
                            id="file"
                            type="file"
                            accept="application/pdf"
                            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                        />
                    </FormGroup>

                    <LoginButton type="submit" disabled={uploading}>
                        {uploading ? "Uploading..." : "Upload Invoice"}
                    </LoginButton>
                </form>
            </Section>

            <Section>
                <h3>Invoices for {moment(currentMonth).format("MMMM YYYY")}</h3>
                {groupedInvoices.length === 0 ? (
                    <p>No invoices for this month.</p>
                ) : (
                    groupedInvoices.map(({ guideName, invoices: guideInvoices }) => (
                        <GuideSection key={guideName}>
                            <GuideHeading>{guideName}</GuideHeading>
                            <InvoiceList>
                                {guideInvoices.map((invoice) => (
                                    <GuideInvoiceRow
                                        key={invoice._id}
                                        invoice={invoice}
                                        onDelete={handleDelete}
                                    />
                                ))}
                            </InvoiceList>
                        </GuideSection>
                    ))
                )}
            </Section>
        </Container>
    );
};

export default AdminDashboardInvoices;
